// Paste a call or a text (decision 142): the transcript of a call (the Plaud, the phone's recorder) or a customer's
// text, pasted in; Claude finds the customer and opens the invoice screen filled in for the person to check and
// Create. The words are kept on the customer's history. Claude only reads here: nothing pasted can move money.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { ASSIST_STATE, type AssistState } from '../components/ClaudeButton';
import { ErrorBox, PageHead } from '../components/ui';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

export function PastePage() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [kind, setKind] = useState<'call' | 'text'>('call');
  const [clipProblem, setClipProblem] = useState(false);
  const read = useMutation({
    mutationFn: () => api.pasteToClaude({ text: text.trim(), kind }),
    onSuccess: (answer) => {
      if (answer.savedTo) void qc.invalidateQueries({ queryKey: qk.activities(answer.savedTo.customerId) });
      if (answer.action) {
        const state: AssistState = { [ASSIST_STATE]: answer.action };
        navigate(answer.action.kind === 'invoice' ? '/invoices/new' : '/stock', { state });
      }
    },
  });
  async function fromClipboard() {
    setClipProblem(false);
    try {
      const got = await navigator.clipboard.readText();
      if (got.trim()) setText(got);
      else setClipProblem(true);
    } catch {
      // the browser said no: a long press in the box pastes instead
      setClipProblem(true);
    }
  }
  const answer = read.data;
  return (
    <div className="stack paste-page">
      <PageHead title={t('pasteTitle')} back="/" />
      <p className="muted">{t('pasteHint')}</p>
      <section className="card stack-sm">
        <div className="chips" role="group" aria-label={t('pasteKind')}>
          <button type="button" className="chip" aria-pressed={kind === 'call'} onClick={() => setKind('call')}>
            {t('pasteCall')}
          </button>
          <button type="button" className="chip" aria-pressed={kind === 'text'} onClick={() => setKind('text')}>
            {t('pasteText')}
          </button>
        </div>
        <button type="button" className="btn btn-secondary btn-block" onClick={() => void fromClipboard()}>
          {t('pasteFromClipboard')}
        </button>
        {clipProblem ? <p className="muted">{t('pasteClipboardNo')}</p> : null}
        <label className="field">
          <span>{kind === 'call' ? t('pasteBoxCall') : t('pasteBoxText')}</span>
          <textarea rows={10} value={text} maxLength={20000} onChange={(e) => setText(e.target.value)} />
        </label>
        {read.error ? <ErrorBox error={read.error} /> : null}
        <button type="button" className="btn btn-primary btn-lg btn-block" disabled={!text.trim() || read.isPending} onClick={() => read.mutate()}>
          {read.isPending ? t('claudeWorking') : t('pasteRead')}
        </button>
        {answer && !answer.action ? (
          <div className="stack-sm" role="status" data-testid="paste-answer">
            <p>{answer.reply}</p>
            {answer.savedTo ? (
              <p className="muted">
                {t('pasteSaved')} <Link to={`/customers/${answer.savedTo.customerId}`}>{answer.savedTo.customerName}</Link>
              </p>
            ) : (
              <p className="muted">{t('pasteNotSaved')}</p>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
}
